import { useState, useEffect } from "react";
import {
  generateInstagramCode,
  verifyInstagram,
  getUserProfile,
  profileUserFromApiResponse,
} from "../api/profile";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/TranslationContext";
import Button from "./ds/Button";
import Icon from "./ds/Icon";
import InlineMessage from "./ds/InlineMessage";
import Modal from "./ds/Modal";
import TextField from "./ds/TextField";

const InstagramVerificationModal = ({ 
  isOpen, 
  setIsOpen,
  setToast,
  isDark,
  onVerificationUpdate,
  onInstagramLinkUpdate, // Callback to update Instagram link in signup form
  initialUsername = "" // Initial username from signup form
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [instagramUsername, setInstagramUsername] = useState(initialUsername);
  const [verification, setVerification] = useState({
    code: null,
    username: null,
    verified: false,
    loading: false,
    verifying: false,
  });


  // Load verification status when modal opens (only if user is authenticated)
  useEffect(() => {
    if (isOpen && initialUsername) {
      // Extract username from Instagram URL if needed
      let username = initialUsername;
      if (initialUsername.includes("instagram.com/")) {
        username = initialUsername.split("instagram.com/")[1]?.split("/")[0]?.split("?")[0] || initialUsername;
      }
      username = username.replace(/^@/, "").trim();
      if (username && !instagramUsername) {
        setInstagramUsername(username);
      }
    }
  }, [isOpen, initialUsername]);

  // Auto-generate code ONLY if username was pre-filled (from existing profile)
  // Don't auto-generate if modal is opened fresh (user needs to enter username first)
  useEffect(() => {
    // Only auto-generate if username is already set AND there's an existing verification code
    if (isOpen && user?.id && instagramUsername && instagramUsername.trim() !== "" && initialUsername) {
      let mounted = true;
      
      const checkExistingVerification = async () => {
        // Prevent multiple calls
        if (verification.loading || verification.code) return;
        
        try {
          // Check if verification already exists
          const response = await getUserProfile(user.id);
          if (!mounted) return;

          const profileUser = profileUserFromApiResponse(response, user.id);
          if (profileUser?.instagramVerification) {
            const instagramVerification = profileUser.instagramVerification;
            if (instagramVerification.code) {
              setVerification({
                code: instagramVerification.code,
                username: instagramVerification.username,
                verified: instagramVerification.verified || false,
                loading: false,
                verifying: false,
              });
              if (instagramVerification.username) {
                setInstagramUsername(instagramVerification.username);
              }
            }
          }
        } catch (error) {
          console.error("Error checking existing verification:", error);
        }
      };
      
      // Small delay to ensure user context is fully updated
      const timer = setTimeout(() => {
        checkExistingVerification();
      }, 500);
      
      return () => {
        mounted = false;
        clearTimeout(timer);
      };
    }
  }, [isOpen, user?.id, initialUsername]);

  const handleGenerateCode = async () => {
    if (!instagramUsername.trim()) {
      setToast(t("toasts.instagramEnterUsername"));
      return;
    }
    
    // If user is not authenticated (signup flow), just update the link and show message
    if (!(user?.id || user?._id)) {
      const username = instagramUsername.replace(/^@/, "").trim();
      const instagramUrl = `https://instagram.com/${username}`;
      if (onInstagramLinkUpdate) {
        onInstagramLinkUpdate(instagramUrl);
      }
      setToast(t("toasts.completeSignupForCode"));
      return;
    }
    
    try {
      setVerification({ ...verification, loading: true });
      const response = await generateInstagramCode(instagramUsername);
      if (response.success) {
        setVerification({
          code: response.code,
          username: response.username,
          verified: false,
          loading: false,
          verifying: false,
        });
        setInstagramUsername(response.username);
        
        // Update Instagram link in signup form if callback provided
        if (onInstagramLinkUpdate) {
          const instagramUrl = `https://instagram.com/${response.username}`;
          onInstagramLinkUpdate(instagramUrl);
        }
        
        setToast(response.instructions);
      }
    } catch (error) {
      setToast(error.response?.data?.message || t("toasts.failedGenerateCode"));
      setVerification({ ...verification, loading: false });
    }
  };

  const handleVerify = async () => {
    try {
      setVerification({ ...verification, verifying: true });
      const response = await verifyInstagram();
      if (response.success && response.verified) {
        setVerification({
          ...verification,
          verified: true,
          verifying: false,
        });
        setToast(t("toasts.instagramVerified"));
        // Notify parent component of verification update
        if (onVerificationUpdate) {
          onVerificationUpdate(true);
        }
        // Reload verification status to ensure sync
        setTimeout(async () => {
          try {
            const statusResponse = await getUserProfile(user.id);
            const profileUser = profileUserFromApiResponse(statusResponse, user.id);
            if (profileUser?.instagramVerification) {
              const instagramVerification = profileUser.instagramVerification;
              setVerification({
                code: instagramVerification.code,
                username: instagramVerification.username,
                verified: instagramVerification.verified || false,
                loading: false,
                verifying: false,
              });
            }
          } catch (error) {
            console.error("Error reloading verification status:", error);
          }
        }, 500);
      } else {
        setToast(response.message || t("toasts.verificationFailed"));
        setVerification({ ...verification, verifying: false });
      }
    } catch (error) {
      setToast(error.response?.data?.message || t("toasts.verificationFailed"));
      setVerification({ ...verification, verifying: false });
    }
  };

  const handleReset = () => {
    setVerification({
      code: null,
      username: null,
      verified: false,
      loading: false,
      verifying: false,
    });
    setInstagramUsername("");
  };

  const updateUsername = (value) => {
    setInstagramUsername(value);
    // Update link in real-time if callback provided (for signup flow)
    if (onInstagramLinkUpdate && value.trim()) {
      const username = value.replace(/^@/, "").replace("https://instagram.com/", "").replace("https://www.instagram.com/", "").trim();
      if (username) onInstagramLinkUpdate(`https://instagram.com/${username}`);
    }
  };

  const STEPS = ["step1", "step2", "step3", "step4", "step5"];

  return (
    <Modal
      open={isOpen}
      onClose={() => setIsOpen(false)}
      title={t("instagramVerify.title")}
      description={!verification.verified && !verification.code ? t("instagramVerify.intro") : undefined}
      closeLabel={t("common.close")}
      isDark={isDark}
    >
      {verification.verified ? (
        <>
          <InlineMessage tone="success" title={t("instagramVerify.verifiedTitle")}>
            {t("instagramVerify.verifiedBody", { username: verification.username })}
          </InlineMessage>
          <div className="flex justify-end">
            <Button variant="gray" onClick={handleReset}>
              {t("instagramVerify.reset")}
            </Button>
          </div>
        </>
      ) : !verification.code ? (
        <form
          className="flex flex-col gap-space-20"
          onSubmit={(e) => {
            e.preventDefault();
            if (!verification.loading && (user?.id || user?._id)) handleGenerateCode();
          }}
        >
          <TextField
            autoFocus
            label={t("instagramVerify.usernameLabel")}
            icon={<Icon name="search" size={20} />}
            placeholder={t("instagramVerify.usernamePlaceholder")}
            value={instagramUsername}
            onChange={(e) => updateUsername(e.target.value)}
          />
          <div className="flex justify-end">
            <Button
              type="submit"
              loading={verification.loading}
              disabled={!instagramUsername.trim() || !(user?.id || user?._id)}
            >
              {verification.loading ? t("instagramVerify.generating") : t("instagramVerify.generate")}
            </Button>
          </div>
        </form>
      ) : (
        <>
          <p className="text-subheadline text-label-secondary">{t("instagramVerify.addCode")}</p>
          <div className="flex flex-col items-center gap-space-4 rounded-radius-md bg-fill-quaternary p-space-16 text-center">
            <p className="text-caps-label uppercase text-label-tertiary">{t("instagramVerify.codeLabel")}</p>
            <p className="text-title-2 text-accent-text">{verification.code}</p>
          </div>
          <div className="flex flex-col gap-space-4">
            <p className="text-footnote-semibold text-label-secondary">{t("instagramVerify.howTo")}</p>
            <ol className="flex list-inside list-decimal flex-col gap-space-4 text-footnote text-label-secondary">
              {STEPS.map((step) => (
                <li key={step}>{t(`instagramVerify.${step}`)}</li>
              ))}
            </ol>
          </div>
          <div className="flex justify-end gap-space-12">
            <Button variant="gray" onClick={handleReset} disabled={verification.verifying}>
              {t("common.cancel")}
            </Button>
            <Button loading={verification.verifying} onClick={handleVerify}>
              {verification.verifying ? t("instagramVerify.verifying") : t("instagramVerify.verify")}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default InstagramVerificationModal;
